import React, { useState, useEffect, useRef } from "react";

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  // ✅ EXACT API URL
  const API_BASE_URL = "https://api.donatepe.org";

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/admin/testimonials`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setTestimonials(data.data);
        } else {
          throw new Error("Failed to fetch testimonials");
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-reveal");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (cardRef.current) observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  const nextSlide = () => {
    if (testimonials.length > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrent((prev) => (prev + 1) % testimonials.length);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const prevSlide = () => {
    if (testimonials.length > 0 && !isAnimating) {
      setIsAnimating(true);
      setCurrent((prev) =>
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 600);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  if (loading) {
    return (
      <section className="relative bg-white py-20 md:py-24 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-96 bg-gradient-to-r from-gray-100 to-gray-200 rounded-3xl"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative bg-white py-20 md:py-24 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center border border-rose-100 shadow-lg">
            <div className="w-20 h-20 mx-auto bg-rose-50 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600">Unable to load testimonials. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <section className="relative bg-white py-20 md:py-24 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 text-center border border-gray-100 shadow-lg">
            <div className="w-20 h-20 mx-auto bg-teal-50 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-gray-600">No testimonials available yet. Be the first to share your story!</p>
          </div>
        </div>
      </section>
    );
  }

  const testimonial = testimonials[current];

  return (
    <section ref={sectionRef} className="relative bg-white py-20 md:py-24 lg:py-28 overflow-hidden">
      
      {/* ===== LIGHT, AIRY BACKGROUND ===== */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-teal-50/30 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-gradient-to-tr from-amber-50/30 via-transparent to-transparent rounded-full blur-3xl animate-float-delay"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-emerald-50/20 via-teal-50/20 to-emerald-50/20 rounded-full blur-3xl animate-pulse-slow"></div>
        
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #14b8a6 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ===== SECTION HEADER ===== */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 opacity-0 animate-header-reveal">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-teal-100 shadow-sm mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
            </span>
            <span className="text-xs font-medium text-teal-700 uppercase tracking-wider">
              Voices of trust
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight mb-4">
            What{' '}
            <span className="relative">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                donors
              </span>
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-gradient-to-r from-teal-200/40 to-emerald-200/40 blur-sm"></span>
            </span>
            {' '}are saying
          </h2>
          
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Real stories from real people who've experienced the power of community
          </p>
        </div>

        {/* ===== MAIN TESTIMONIAL CARD ===== */}
        {testimonials.length > 0 && (
          <div 
            ref={cardRef}
            className="relative max-w-5xl mx-auto opacity-0 translate-y-8 animate-card-reveal"
          >
            
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-20 h-20 bg-teal-100/30 rounded-full blur-2xl animate-float-slow"></div>
            <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-amber-100/30 rounded-full blur-2xl animate-float-delay"></div>
            
            {/* Navigation Arrows - Only show if more than 1 testimonial */}
            {testimonials.length > 1 && (
              <>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-20">
                  <button
                    onClick={prevSlide}
                    disabled={isAnimating}
                    className="group relative w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500"></div>
                    <svg 
                      className="w-5 h-5 lg:w-6 lg:h-6 mx-auto text-gray-600 group-hover:text-teal-600 transition-colors group-hover:-translate-x-0.5 transition-transform duration-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                </div>

                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-20">
                  <button
                    onClick={nextSlide}
                    disabled={isAnimating}
                    className="group relative w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg hover:shadow-xl transition-all duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500"></div>
                    <svg 
                      className="w-5 h-5 lg:w-6 lg:h-6 mx-auto group-hover:translate-x-0.5 transition-transform duration-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </>
            )}

            {/* ===== TESTIMONIAL CARD - TALLER HEIGHT ===== */}
            <div className={`
              relative bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-2xl border border-white/60
              transition-all duration-600 ease-out
              ${isAnimating ? 'opacity-60 scale-95' : 'opacity-100 scale-100'}
            `}>
              
              {/* Gradient accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-teal-500 to-emerald-500"></div>
              
              <div className="flex flex-col lg:flex-row min-h-[500px] lg:min-h-[550px]">
                
                {/* ===== LEFT - IMAGE SECTION ===== */}
                <div className="lg:w-[45%] relative overflow-hidden bg-gradient-to-br from-teal-50 to-emerald-50">
                  
                  <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `radial-gradient(circle at 10px 10px, #14b8a6 2px, transparent 2px)`,
                    backgroundSize: '30px 30px'
                  }}></div>
                  
                  <div className="relative h-full min-h-[300px] lg:min-h-full">
                    <img
                      src={getImageUrl(testimonial.image_url)}
                      alt={testimonial.title}
                      className="w-full h-full object-cover transition-transform duration-10000 hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1494790108755-2714420cc6d5?w=800&h=1000&fit=crop";
                      }}
                    />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-gray-900/20 to-transparent lg:bg-gradient-to-r lg:from-gray-900/60 lg:via-gray-900/30 lg:to-transparent"></div>
                    
                    <div className="absolute top-6 left-6 text-6xl text-white/30 font-serif">"</div>
                    
                    <div className="absolute bottom-6 left-6">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-medium text-gray-700">Verified supporter</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* ===== RIGHT - CONTENT SECTION ===== */}
                <div className="lg:w-[55%] p-8 lg:p-10 xl:p-12 flex flex-col justify-center">
                  
                  {/* Title - Dynamic from API */}
                  <h3 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {testimonial.title}
                  </h3>
                  
                  {/* Testimonial content - Dynamic from API */}
                  <div className="relative mb-8">
                    <span className="absolute -top-3 -left-2 text-4xl text-teal-200/50 font-serif">"</span>
                    <p className="text-gray-600 text-base lg:text-lg leading-relaxed pl-4 pt-2">
                      {testimonial.description}
                    </p>
                    <span className="absolute -bottom-5 -right-2 text-4xl text-emerald-200/50 font-serif rotate-180">"</span>
                  </div>
                  
                  {/* Author info - Dynamic from API */}
                  <div className="flex items-center justify-between mt-4 pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-600 to-emerald-600 flex items-center justify-center text-white text-lg font-bold shadow-lg">
                        {testimonial.title?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{testimonial.title}</p>
                        <p className="text-sm text-gray-500">Verified donor</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== DOT NAVIGATION - ONLY SHOW IF MORE THAN 1 ===== */}
        {testimonials.length > 1 && (
          <div className="flex items-center justify-center mt-12">
            <div className="flex items-center gap-3 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-100 shadow-sm">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full blur-md transition-all duration-500 ${
                    index === current ? 'opacity-60 scale-150' : 'opacity-0 group-hover:opacity-40'
                  }`}></div>
                  <div className={`relative transition-all duration-500 ${
                    index === current
                      ? 'w-10 h-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 rounded-full'
                      : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400 rounded-full'
                  }`}></div>
                </button>
              ))}
              
              <span className="text-xs text-gray-400 ml-2">
                {current + 1} / {testimonials.length}
              </span>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes headerReveal {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-header-reveal {
          animation: headerReveal 0.8s cubic-bezier(0.2, 0.9, 0.3, 1) forwards;
        }
        
        @keyframes cardReveal {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-card-reveal {
          animation: cardReveal 0.9s cubic-bezier(0.23, 1, 0.32, 1) 0.2s forwards;
        }
        
        @keyframes reveal {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        
        .animate-reveal {
          animation: reveal 0.6s ease forwards;
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.3;
          }
          33% { 
            transform: translate(20px, -15px) scale(1.05); 
            opacity: 0.4;
          }
          66% { 
            transform: translate(-15px, 10px) scale(0.95); 
            opacity: 0.35;
          }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delay {
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .animate-float-slow {
          animation: float 30s ease-in-out infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.3; 
            transform: scale(1.05); 
          }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        .duration-600 {
          transition-duration: 600ms;
        }
        
        .duration-10000 {
          transition-duration: 10000ms;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;